import { Link } from "react-router-dom";
import { Bike, ArrowRight, Check, Wrench, CalendarDays, KeyRound, PenLine, MessageCircle, ShoppingCart, FileText, Bell, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DemoTour } from "@/components/DemoTour";
import { APP_NAME, APP_TAGLINE, PREZZO_MESE, PREZZO_ANNO, GIORNI_PROVA, TESTIMONIANZA_FOTO, CONTATTO_WHATSAPP, MESSAGGIO_CONTATTO, RECENSIONI } from "@/lib/brand";
import { Quote, Store, Star } from "lucide-react";

const WA_URL = `https://wa.me/${CONTATTO_WHATSAPP}?text=${encodeURIComponent(MESSAGGIO_CONTATTO)}`;

const WhatsAppFisso = () => (
  <a href={WA_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp-float" aria-label="Scrivici su WhatsApp"
    className="fixed bottom-5 right-5 z-30 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-xl transition-transform duration-200 hover:-translate-y-0.5">
    <MessageCircle className="h-5 w-5" /><span className="hidden sm:inline">Domande? Scrivici</span>
  </a>
);

const Recensioni = () => (RECENSIONI.length === 0 ? null : (
  <section className="bg-white py-20" data-testid="recensioni-section">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Dicono di VLR BIKE</p>
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">I clienti se ne accorgono</h2>
        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700" data-testid="recensioni-media"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />4,6 su Google</p>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {RECENSIONI.map((r, k) => (
          <figure key={r.nome} className="rounded-xl border border-slate-200 p-6" data-testid={`recensione-${k}`}>
            <Quote className="h-6 w-6 text-orange-500" />
            <blockquote className="mt-4 text-sm leading-relaxed text-slate-700">“{r.testo}”</blockquote>
            <figcaption className="mt-4 text-sm"><span className="font-semibold text-slate-900">{r.nome}</span><span className="text-slate-500"> · Recensione Google</span></figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
));

const IMG = "https://images.unsplash.com/photo-1605271864611-58dd08d10547?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FUNZIONI = [
  { icon: Wrench, t: "Officina", d: "Schede di lavoro con ricambi, manodopera, stati e stampa." },
  { icon: FileText, t: "Preventivi", d: "Preventivi in PDF, firmati e trasformati in scheda in un clic." },
  { icon: ShoppingCart, t: "Cassa e magazzino", d: "Vendita al banco, metodi di pagamento e scorte sempre aggiornate." },
  { icon: KeyRound, t: "Noleggi", d: "Flotta, calendario settimanale, contratti e cauzioni." },
  { icon: CalendarDays, t: "Agenda", d: "Appuntamenti di officina e noleggio, giorno per giorno." },
  { icon: PenLine, t: "Firma su tablet", d: "Privacy e contratti firmati sullo schermo e archiviati." },
  { icon: MessageCircle, t: "WhatsApp", d: "Bici pronta, promemoria e documenti inviati con un tocco." },
  { icon: Bell, t: "Tagliandi", d: "Chi non passa da 6 mesi? Lo ricontatti subito." },
];

const INCLUSO = ["Utenti staff illimitati", "Clienti, bici e schede illimitate", "Preventivi e contratti in PDF", "Cassa, magazzino e noleggi", "Firma su tablet e archivio documenti", "Aggiornamenti e assistenza via email"];

const FAQ = [
  ["Serve la carta di credito per provare?", `No. Hai ${GIORNI_PROVA} giorni gratis senza inserire alcun metodo di pagamento.`],
  ["Cosa succede alla fine della prova?", "Ti avvisiamo via email 3 giorni prima. Se attivi l'abbonamento continui senza perdere nulla, altrimenti l'accesso si sospende e i dati restano salvati."],
  ["Posso disdire quando voglio?", "Sì, l'abbonamento è mensile e senza vincoli."],
  ["Funziona su tablet e smartphone?", "Sì, si usa dal browser su computer, tablet e telefono."],
];

const Cta = ({ id, className = "" }) => (
  <Button asChild className={`h-12 rounded-full bg-orange-600 px-7 text-base font-semibold hover:bg-orange-700 ${className}`}>
    <Link to="/registrati" data-testid={id}>Prova gratis {GIORNI_PROVA} giorni <ArrowRight className="ml-1 h-4 w-4" /></Link>
  </Button>
);

const Header = () => (
  <header className="absolute inset-x-0 top-0 z-20">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
      <div className="flex items-center gap-2.5 text-white">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-600"><Bike className="h-5 w-5" /></div>
        <span className="font-heading text-lg font-extrabold" data-testid="landing-brand">{APP_NAME}</span>
      </div>
      <nav className="flex items-center gap-2 text-sm sm:gap-6">
        <a href="#prezzi" className="hidden text-slate-200 hover:text-white sm:inline" data-testid="landing-nav-prezzi">Prezzi</a>
        <a href="#demo" className="hidden text-slate-200 hover:text-white sm:inline" data-testid="landing-nav-demo">Demo</a>
        <Link to="/login" className="rounded-full border border-white/30 px-4 py-1.5 font-semibold text-white hover:bg-white/10" data-testid="landing-login-link">Accedi</Link>
      </nav>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-slate-950 pb-24 pt-32 sm:pb-32 sm:pt-40">
    <img src={IMG} alt="Officina bici" className="absolute inset-0 h-full w-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
    <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
      <p className="fade-up mb-5 text-xs font-bold uppercase tracking-[0.25em] text-orange-400">{APP_TAGLINE}</p>
      <h1 className="fade-up max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
        Officina, cassa e noleggi.<br /><span className="text-orange-500">Finalmente in un posto solo.</span>
      </h1>
      <p className="fade-up mt-6 max-w-xl text-base text-slate-300 sm:text-lg" style={{ animationDelay: "160ms" }}>
        Schede di lavoro, preventivi in PDF, firme su tablet e messaggi WhatsApp ai clienti. Pensato per chi ripara e vende bici, non per i contabili.
      </p>
      <div className="fade-up mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "240ms" }}>
        <Cta id="hero-prova-gratis-btn" />
        <span className="text-sm text-slate-400">Nessuna carta richiesta · poi {PREZZO_MESE} €/mese</span>
      </div>
    </div>
  </section>
);

const Funzioni = () => (
  <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
    <div className="grid gap-12 lg:grid-cols-[1fr_2fr]">
      <div>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Tutto incluso</p>
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Il banco di lavoro digitale del tuo negozio</h2>
        <p className="mt-4 text-slate-600">Ogni funzione nasce dal lavoro vero di un'officina di bici.</p>
      </div>
      <div className="grid gap-px overflow-hidden rounded-xl bg-slate-200 sm:grid-cols-2">
        {FUNZIONI.map((f) => (
          <div key={f.t} className="group bg-white p-6 transition-colors duration-200 hover:bg-orange-50" data-testid={`feature-${f.t.toLowerCase().replace(/\s+/g, "-")}`}>
            <f.icon className="h-6 w-6 text-orange-600 transition-transform duration-200 group-hover:-translate-y-0.5" />
            <h3 className="mt-4 font-heading font-bold text-slate-900">{f.t}</h3>
            <p className="mt-1 text-sm text-slate-600">{f.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Demo = () => (
  <section id="demo" className="bg-slate-100 py-20 sm:py-28">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Demo</p>
      <h2 className="mb-10 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Guarda come funziona e cosa ti fa risparmiare</h2>
      <DemoTour />
    </div>
  </section>
);

const FotoNegozio = () => (TESTIMONIANZA_FOTO
  ? <img src={TESTIMONIANZA_FOTO} alt="Il negozio VLR BIKE" className="h-full min-h-[280px] w-full object-cover" data-testid="testimonianza-foto" />
  : <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 bg-slate-800 text-slate-400" data-testid="testimonianza-foto-placeholder"><Store className="h-12 w-12" /><span className="text-xs">Foto del negozio VLR BIKE</span></div>);

const Testimonianza = () => (
  <section className="bg-slate-950 py-20 sm:py-28" data-testid="testimonianza-section">
    <div className="mx-5 grid max-w-6xl overflow-hidden rounded-2xl bg-slate-900 sm:mx-8 xl:mx-auto lg:grid-cols-[1fr_1.4fr]">
      <FotoNegozio />
      <figure className="p-8 sm:p-12">
        <Quote className="h-10 w-10 text-orange-500" />
        <p className="mt-6 font-heading text-2xl font-extrabold leading-tight text-white sm:text-3xl" data-testid="testimonianza-titolo">Un gestionale pensato per chi ripara le bici, da chi ripara le bici.</p>
        <blockquote className="mt-6 space-y-4 text-base leading-relaxed text-slate-300" data-testid="testimonianza-testo">
          <p>“Prima avevo schede di carta, quaderni e messaggi sparsi. Ora officina, noleggi e cassa stanno in un solo posto, e i clienti ricevono preventivi e avvisi su WhatsApp in un attimo.”</p>
          <p>“Finalmente un gestionale pensato per chi ripara bici: semplice, veloce e sempre a portata di mano anche dal tablet in officina.”</p>
        </blockquote>
        <figcaption className="mt-8 border-t border-slate-700 pt-5" data-testid="testimonianza-autore">
          <p className="font-heading font-bold text-white">Paolo Valerio</p>
          <p className="text-sm text-orange-400">Titolare, VLR BIKE</p>
        </figcaption>
      </figure>
    </div>
  </section>
);

const Prezzi = () => (
  <section id="prezzi" className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
    <div className="grid items-center gap-12 lg:grid-cols-2">
      <div>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Prezzi</p>
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Prezzi chiari. Niente sorprese.</h2>
        <p className="mt-4 max-w-md text-slate-600">Paghi ogni mese e disdici quando vuoi, oppure scegli l'annuale e risparmi. I tuoi dati sono sempre separati e riservati al tuo negozio.</p>
        <p className="mt-6 flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Pagamenti sicuri con carta o PayPal</p>
      </div>
      <div className="rounded-2xl bg-slate-950 p-8 text-white shadow-2xl sm:p-10" data-testid="pricing-card">
        <p className="text-sm font-semibold text-orange-400">{APP_NAME} Completo</p>
        <p className="mt-3 font-heading text-6xl font-extrabold" data-testid="pricing-price">{PREZZO_MESE} €<span className="text-lg font-semibold text-slate-400"> /mese</span></p>
        <p className="mt-2 text-sm text-slate-400">Primi {GIORNI_PROVA} giorni gratis</p>
        <p className="mt-4 rounded-lg bg-white/5 px-4 py-3 text-sm" data-testid="pricing-annuale">Oppure <b className="text-white">{PREZZO_ANNO} €/anno</b> <span className="ml-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-bold">risparmi {PREZZO_MESE * 12 - PREZZO_ANNO} €</span></p>
        <ul className="my-8 space-y-3">
          {INCLUSO.map((x) => <li key={x} className="flex items-start gap-3 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />{x}</li>)}
        </ul>
        <Cta id="pricing-prova-gratis-btn" className="w-full" />
      </div>
    </div>
  </section>
);

const Domande = () => (
  <section className="border-t border-slate-200 bg-white py-20">
    <div className="mx-auto max-w-3xl px-5 sm:px-8">
      <h2 className="mb-8 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Domande frequenti</h2>
      <Accordion type="single" collapsible data-testid="faq-list">
        {FAQ.map(([q, a], k) => (
          <AccordionItem key={q} value={`f${k}`}>
            <AccordionTrigger className="text-left font-semibold" data-testid={`faq-trigger-${k}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <p className="mt-8 text-sm text-slate-600">Hai altre domande? <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#128C4A] hover:underline" data-testid="faq-whatsapp-link">Scrivici su WhatsApp</a>, ti rispondiamo noi.</p>
    </div>
  </section>
);

const Chiusura = () => (
  <section className="bg-orange-600 py-16">
    <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-5 sm:px-8 md:flex-row md:items-center">
      <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">Pronto a mettere in ordine l'officina?</h2>
      <Button asChild className="h-12 rounded-full bg-slate-950 px-7 text-base font-semibold hover:bg-slate-800">
        <Link to="/registrati" data-testid="footer-prova-gratis-btn">Inizia la prova gratuita <ArrowRight className="ml-1 h-4 w-4" /></Link>
      </Button>
    </div>
  </section>
);

export default function Presentazione() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header />
      <Hero />
      <Funzioni />
      <Demo />
      <Testimonianza />
      <Recensioni />
      <Prezzi />
      <Domande />
      <Chiusura />
      <WhatsAppFisso />
      <footer className="bg-slate-950 py-8 text-center text-xs text-slate-500">© {new Date().getFullYear()} {APP_NAME} · {APP_TAGLINE}</footer>
    </div>
  );
}
